/**
 * База знаний КЦ (`/knowledge`): карточки с версиями, замечания (текст и голос), предложения модели,
 * «Спросить базу», вопросы клиентов и пробелы. Спека `docs/specs/2026-10-25-knowledge-editor-design.md`.
 */
import { apiFetch } from "@/lib/api";
import type { VoiceRecord } from "@/lib/voice";

export type Sync = { state: string; label: string; error: string; live_rev: number | null; rev: number };

export type Card = {
  id: number;
  title: string;
  body: string;
  kind: string;
  topic: string;
  location_id: number | null;
  version: number;
  status: string;
  origin: string | null;
  merged_into: number | null;
  sync: Sync;
  comments: number;
  proposals: number;
  questions: number;
  modified: string;
};

export type Version = {
  number: number;
  title: string;
  body: string;
  kind: string;
  topic: string;
  location_id: number | null;
  author_kind: string;
  user_id: number | null;
  reason: string;
  date: string;
};

export type Comment = {
  id: number;
  card_id: number;
  source: string;
  author: string;
  text: string;
  voice: boolean;
  /** Запись голоса из кабинета (`/api/voice/{id}/file`); null — без неё. */
  voice_id: number | null;
  status: string;
  version: number;
  proposal_id: number | null;
  question_id: number | null;
  /** Текст вопроса клиента, к ответу на который замечание (только в карточке). */
  question?: string | null;
  date: string;
};

export type Proposal = {
  id: number;
  card_id: number | null;
  title: string;
  body: string;
  kind: string;
  topic: string;
  location_id: number | null;
  reason: string;
  base_version: number;
  source: string;
  status: string;
  /** revision — новая редакция; recommend_reject — модель советует не менять. */
  result: string;
  date: string;
  card_title?: string;
};

export type CardDetail = Card & {
  versions: Version[];
  comments_list: Comment[];
  proposals_list: Proposal[];
  questions_list: { id: number; text: string; hits: number }[];
};

export type Work = {
  proposals: Proposal[];
  comments: { card_id: number; card_title: string; count: number }[];
  waiting_voice: number;
  failed: Card[];
  gaps: number;
};

export type QuestionCategory = { code: string; label: string; kind: string; hint: string };
export type Meta = {
  topics: string[];
  kinds: string[];
  categories: QuestionCategory[];
  question_kinds: Record<string, string>;
  can_edit: boolean;
};

export type Question = {
  id: number;
  text: string;
  topic: string;
  category: string;
  kind: string;
  hits: number;
  unanswered: number;
  channels: string[];
  last: string;
  card: boolean;
  cards: { id: number; title: string }[];
  /** Сколько ответов сотрудников (`kb_question_answer`). */
  answers: number;
  /** Есть ли мой ответ (текст или голос). */
  mine: boolean;
  /** Я отметил свой ответ полным. */
  mine_done: boolean;
  /** Мой ответ: none — нет · open — не полный · done — полный · used — вошёл в карточку. Мой — текущий
   * `new`, а без него — последний вошедший в карточку. */
  mine_status: MineStatus;
};

export type MineStatus = "none" | "open" | "done" | "used";
export type MineCounts = { all: number; none: number; open: number; done: number };
export type QuestionList = { episodes: number; items: Question[]; counts: MineCounts; total: number };

/** Формулировка в группе: linked — привязана к карточке, suggested — похожа на её вопросы (предположение),
 * member — участник будущей группы или разовый; match — совпала с поиском. */
export type GroupMember = Question & { relation: "linked" | "suggested" | "member"; match: boolean };
/** Группа вида «По группам» (`kb/groups.py`): card — карточка и её вопросы; future — будущая карточка
 * (близкие по смыслу без карточки); live/action — то же, но ответ — данные клиента / действие в CRM;
 * single — разовый вопрос. Ответ на группу — к `representative_id`. */
export type QuestionGroup = {
  key: string;
  type: "card" | "future" | "live" | "action" | "single";
  category: string;
  kind: string;
  card: { id: number; title: string } | null;
  representative_id: number;
  title: string;
  representative_text: string;
  mentions: number;
  unanswered: number;
  question_count: number;
  linked_count: number;
  suggested_count: number;
  mine_status: MineStatus;
  match: boolean;
  members: GroupMember[];
};
export type GroupCategory = {
  code: string;
  mentions: number;
  groups: number;
  with_card: number;
  singles: number;
  items: QuestionGroup[];
};
export type GroupList = {
  episodes: number;
  categories: GroupCategory[];
  counts: MineCounts;
  total: number;
  /** Заголовочные id групп без моего полного ответа — «Пройти очередь». */
  queue: number[];
  totals: { questions: number; mentions: number; card_groups: number; future_groups: number; suggested: number; singles: number };
};

/** Шапка страницы вопроса `/knowledge/q/<id>`: частота за период, мой статус, действующие карточки. */
export type QuestionDetail = Omit<Question, "cards" | "card"> & {
  status: string;
  /** Вопрос слит — страница переходит на главный. */
  merged_into: number | null;
  /** Случаев за всё время и первый из них. */
  hits_all: number;
  first: string | null;
  /** Чатов и звонков с вопросом за период — кнопки в «Чаты» (`/chats?kbq=`). */
  chats: number;
  calls: number;
  cards: Card[];
  can_edit: boolean;
};

export type Hit = {
  id: number;
  source: "chat" | "call";
  source_id: number;
  message_id: number | null;
  channel: string;
  date: string;
  quote: string;
  answer: string;
  evidence: string;
  answered: string;
  verified: boolean;
};

export type Answer = {
  answer: string;
  sources: { card_id: number; title: string; rev: number; version: number; actual: boolean; status: string }[];
  other_sources: string[];
};

/** Ответ сотрудника на вопрос клиента: сырьё карточки. Сотрудник видит свой, редактор — все. */
export type QuestionAnswer = {
  id: number;
  question_id: number;
  text: string;
  voice: boolean;
  /** Записи голоса: файл сохранён сразу, текст пишет фон (в `text` не попадает). */
  voices: AnswerVoice[];
  /** Сотрудник отметил: ответ полный. */
  done: boolean;
  /** Сколько редакций текста (правки за 10 минут — одна). */
  revisions: number;
  /** new — ждёт карточки · used — вошёл в карточку `card_id`. */
  status: "new" | "used";
  card_id: number | null;
  /** Редактор отобрал: годится для карточки. */
  selected: boolean;
  selected_date: string | null;
  /** Ответ правили после отбора — перечитать. */
  changed_after_select: boolean;
  user_id: number | null;
  author: string;
  date: string;
};

/** Запись голоса у ответа — общий модуль (`@/lib/voice`). */
export type AnswerVoice = VoiceRecord;
export type AnswerRevision = { id: number; text: string; user_id: number | null; date: string };

export type Gap = { id: number; question_id: number; text: string; hits: number; reason: string; answers: QuestionAnswer[] };

const json = (body: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body) });

export const loadMeta = () => apiFetch<Meta>("/api/knowledge/meta");
export const loadWork = () => apiFetch<Work>("/api/knowledge/work");
export const loadCards = (q: string, topic: string, status: string) =>
  apiFetch<Card[]>(
    `/api/knowledge/cards?${new URLSearchParams({ q, topic, status }).toString()}`,
  );
export const loadCard = (id: number) => apiFetch<CardDetail>(`/api/knowledge/cards/${id}`);
/** `mine`: "" — все · none — без моего ответа · open — мой не полный · done — мой полный (и в карточке). */
/** `q` — ключевые слова: все должны быть в тексте вопроса (окончания длинных слов срезаны на сервере). */
export const loadQuestions = (days: number, unanswered: boolean, noCard: boolean, category = "", mine = "", q = "") =>
  apiFetch<QuestionList>(
    `/api/knowledge/questions?days=${days}&unanswered=${unanswered}&no_card=${noCard}&category=${category}&mine=${mine}&q=${encodeURIComponent(q)}&limit=2000`,
  );
/** Вид «По группам»: те же отборы, что у списка; счёт, отборы и очередь — по группам. */
export const loadQuestionGroups = (days: number, unanswered: boolean, noCard: boolean, category = "", mine = "", q = "") =>
  apiFetch<GroupList>(
    `/api/knowledge/question-groups?days=${days}&unanswered=${unanswered}&no_card=${noCard}&category=${category}&mine=${mine}&q=${encodeURIComponent(q)}`,
  );
export const linkQuestions = (cardId: number, questionIds: number[]) =>
  apiFetch<{ linked: number }>(`/api/knowledge/cards/${cardId}/question-links`, json({ question_ids: questionIds }));
/** `cardId` — «Не то» (не предлагать к этой карточке); `null` — «Вынести вопрос» из будущей группы. */
export const rejectInGroup = (questionId: number, cardId: number | null) =>
  apiFetch(`/api/knowledge/questions/${questionId}/group-reject`, json({ card_id: cardId }));
export const loadQuestion = (id: number, days: number) =>
  apiFetch<QuestionDetail>(`/api/knowledge/questions/${id}?days=${days}`);
export const loadHits = (id: number, days: number) =>
  apiFetch<{ cards: { id: number; title: string }[]; hits: Hit[] }>(
    `/api/knowledge/questions/${id}/hits?days=${days}`,
  );
export const loadGaps = () => apiFetch<Gap[]>("/api/knowledge/gaps");
export const loadAnswers = (questionId: number) =>
  apiFetch<QuestionAnswer[]>(`/api/knowledge/questions/${questionId}/answers`);
/** Текст своего ответа (автосохранение): новый или правка ответа, ещё не вошедшего в карточку. */
export const saveAnswer = (questionId: number, text: string) =>
  apiFetch<QuestionAnswer>(`/api/knowledge/questions/${questionId}/answers`, json({ text }));
/** Загруженную запись (`uploadVoice`) — к своему ответу. Повтор с тем же `voiceId` безопасен: сервер
 * вернёт тот же ответ, файл второй раз не грузим. */
export const attachAnswerVoice = (questionId: number, voiceId: number) =>
  apiFetch<QuestionAnswer>(`/api/knowledge/questions/${questionId}/answers/voice`, json({ voice_id: voiceId }));
/** Редактор: ответ «годится для карточки». */
export const selectAnswer = (answerId: number, selected: boolean) =>
  apiFetch<QuestionAnswer>(`/api/knowledge/answers/${answerId}/select`, json({ selected }));
export const deleteAnswerVoice = (voiceId: number) =>
  apiFetch<QuestionAnswer>(`/api/knowledge/answers/voice/${voiceId}`, { method: "DELETE" });
export const markAnswerDone = (questionId: number, done: boolean) =>
  apiFetch<QuestionAnswer>(`/api/knowledge/questions/${questionId}/answers/done`, json({ done }));
export const loadAnswerRevisions = (answerId: number) =>
  apiFetch<AnswerRevision[]>(`/api/knowledge/answers/${answerId}/revisions`);
export const answerVoiceUrl = (voiceId: number) => `/api/voice/${voiceId}/file`;

export type Content = { title: string; body: string; kind: string; topic: string; location_id: number | null };

export const saveCard = (id: number, c: Content, expected_version: number, reason: string) =>
  apiFetch<{ version: number }>(`/api/knowledge/cards/${id}`, {
    method: "PUT",
    body: JSON.stringify({ ...c, expected_version, reason }),
  });
/** `questionIds` — сразу привязать вопросы (карточка по группе «По группам»). */
export const createCard = (c: Content, reason: string, questionIds: number[] = []) =>
  apiFetch<{ id: number }>("/api/knowledge/cards", json({ ...c, reason, question_ids: questionIds }));
export const rollback = (id: number, number: number, expected_version: number) =>
  apiFetch<{ version: number }>(`/api/knowledge/cards/${id}/rollback`, json({ number, expected_version }));
export const archive = (id: number, expected_version: number, reason: string) =>
  apiFetch(`/api/knowledge/cards/${id}/archive`, json({ expected_version, reason }));
export const retrySync = (id: number) => apiFetch(`/api/knowledge/cards/${id}/sync/retry`, json({}));
/** Замечание к карточке; с `voiceId` — голосом (запись уже в `/api/voice`, текст допишет фон). */
export const addComment = (id: number, text: string, version: number, questionId?: number, voiceId?: number) =>
  apiFetch<Comment>(
    `/api/knowledge/cards/${id}/comments`,
    json({ text, voice: voiceId !== undefined, voice_id: voiceId ?? null, version, question_id: questionId ?? null }),
  );
export const propose = (id: number) => apiFetch<Proposal>(`/api/knowledge/cards/${id}/propose`, json({}));
export const accept = (id: number, edit?: { title?: string; body?: string }) =>
  apiFetch<{ version: number }>(`/api/knowledge/proposals/${id}/accept`, json(edit ?? {}));
export const reject = (id: number) => apiFetch(`/api/knowledge/proposals/${id}/reject`, json({}));
export const ask = (question: string) => apiFetch<Answer>("/api/knowledge/ask", json({ question }));

/** Каналы случаев человеческими словами. */
export const CHANNELS: Record<string, string> = {
  avito: "Авито",
  call_in: "входящий звонок",
  call_out: "исходящий звонок",
  vk: "ВКонтакте",
  livechat: "чат на сайте",
  max: "MAX",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  telegram: "Telegram",
};
export const channelName = (c: string) => CHANNELS[c] ?? c;

export const KINDS: Record<string, string> = { answer: "ответ клиенту", fact: "факт", process: "процесс" };
export const AUTHORS: Record<string, string> = { model: "модель", user: "редактор", voice: "голос", import: "перенос" };
export const COMMENT_STATUS: Record<string, string> = {
  new: "ждёт сборки",
  in_proposal: "в предложении",
  waiting_voice: "ждёт расшифровки",
  done: "учтено",
  note: "одобрение",
  rejected: "отклонено",
};

/** «Основания» карточки из контроля качества (`GET /api/qc/cards/{id}`, ручка соседнего раздела /qc). */
export type QcRemark = { id: number; author: string; chat_id: number | null; mark: string; text: string; transcript: string; date: string };
export type QcBasis = { versions: { number: number; remarks: QcRemark[]; confirmed: QcRemark[] }[] };
export const loadQcBasis = (id: number) => apiFetch<QcBasis>(`/api/qc/cards/${id}`);
